import Popover from "@material-ui/core/Popover";
import * as turf from "@turf/turf";
import * as _ from "lodash";
import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../App.css";
import { State } from "../context/State";
import { Emoji } from "../ui/emoji";
import { ToastUndo } from "../ui/undo";
import "./component.css";
import { LayerColorPicker } from "./LayerColorPicker";
import { LayerInput } from "./LayerInput";
import { PopoverContent } from "./PopoverContent";
import { RandomFeatures } from "./RandomFeatures";
import {
  copyToClipboard,
  fitBounds,
  getName,
  partition,
  saveState,
  libraryUrl
} from "./util";
import Descriptor from "./Descriptor";
import Link from "./Link";
import Tutorial from "./Tutorial";

export function Primary() {
  const {
    layers,
    setLayers,
    addLayer,
    view,
    setView,
    state,
    setLayerProp,
    getLayerProp,
    removeMetaById,
    showLibrary,
    showTutorial,
    setShowTutorial
  } = useContext(State);

  const [_active, setActiveId] = useState(null);
  const [activeLayer, activeId] = _active ? _active : [null, null];
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showText, setShowText] = useState({});

  const handleClose = () => {
    setAnchorEl(null);
    setActiveId(null);
  };

  const seeAll = () => {
    // bbox extent in minX, minY, maxX, maxY order
    const bbs = layers.map(turf.bbox);
    const minX = _.min(bbs.map(a => a[0]));
    const minY = _.min(bbs.map(a => a[1]));
    const maxX = _.max(bbs.map(a => a[2]));
    const maxY = _.max(bbs.map(a => a[3]));
    const bb = [minX, minY, maxX, maxY];
    fitBounds({ layer: turf.bboxPolygon(bb), view, setView, showLibrary });
  };

  const toggleHidden = layer => {
    setLayerProp(layer, {
      hidden: !getLayerProp(layer, "hidden")
    });
  };

  const toggleText = id => {
    const decision = showText[id] != null ? !showText[id] : true;
    const merge = { [id]: decision };
    console.log(decision);
    setShowText({ ...showText, ...merge });
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  return (
    <div className="margin ">
      <div className="content radius-all shadow">
        <Descriptor />
        <div className="padding">
          <div className="primary-content">
            <LayerInput />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ marginBottom: 10 }}>
                {layers.length} Layer{layers.length !== 1 && "s"}
              </div>
              <div style={{ marginBottom: 10 }}>
                <Link
                  hoverDesc="Move map to view all layers"
                  onClick={seeAll.bind(this)}
                >
                  See All
                </Link>
              </div>
            </div>
            <div
              className="layers-holder"
              style={{
                marginBottom: 20,
                height: "50vh",
                overflowY: "scroll"
              }}
            >
              {layers.map((layer, i) => {
                const layerName = getName(layer, i + 1);
                const lText = showText[layer._id];
                return (
                  <div key={`${i}-${lText}`} className="layer-holder">
                    <LayerColorPicker
                      style={{ marginRight: 5, marginTop: 3 }}
                      layer={layer}
                      float={"left"}
                    />
                    <div
                      className="layer-name"
                      style={{
                        whiteSpace: lText === true ? "wrap" : "nowrap",
                        height: lText === true ? "fit-content" : "1.1rem",
                        flex: "1",
                        backgroundColor: activeId === i ? "#d1d1d1" : "#fff"
                      }}
                      onClick={e => {
                        if (i === activeId) {
                          setActiveId(null);
                        } else {
                          setActiveId([layer, i]);
                          setAnchorEl(e.currentTarget);
                        }
                        if (showTutorial) {
                          setShowTutorial();
                          saveState({ ...state, showTutorial: false });
                        }
                      }}
                    >
                      {layerName}
                    </div>
                    <div className="layer-commands">
                      {layerName.length > 30 && (
                        <Link
                          style={{ marginRight: 7 }}
                          onClick={() => toggleText(layer._id)}
                          hoverDesc={"Toggle text wrapping"}
                        >
                          T
                        </Link>
                      )}
                      <Emoji
                        name={getLayerProp(layer, "hidden") ? "monkey" : "eyes"}
                        className="margin-r25"
                        onClick={() => toggleHidden(layer)}
                      />
                      <Emoji
                        name="planeTakeoff"
                        className="margin-r25"
                        onClick={() => {
                          fitBounds({ layer, view, setView, showLibrary });
                        }}
                      />

                      <Emoji
                        name="copy"
                        className="margin-r25"
                        onClick={() => {
                          copyToClipboard(JSON.stringify(layer));
                          toast("Copied to clipboard!", {
                            position: toast.POSITION.BOTTOM_RIGHT
                          });
                        }}
                      />

                      <Emoji
                        name="delete"
                        onClick={() => {
                          const [keep, go] = partition(
                            layers,
                            (_, index) => index !== i
                          );

                          setLayers(keep);
                          saveState({ ...state, layers: keep }, false);
                          go.forEach(l => removeMetaById(l._id));
                          toast(
                            <ToastUndo
                              id={i}
                              undo={() =>
                                addLayer({
                                  layer: go.length === 1 ? go[0] : layers
                                })
                              }
                            />,
                            { position: toast.POSITION.BOTTOM_RIGHT }
                          );
                        }}
                      />
                      {/* Close actions */}
                    </div>
                    {/* Close layer */}
                  </div>
                );
              })}
              {/* Close holder */}
              {showTutorial && <Tutorial />}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div></div>
              <RandomFeatures />
            </div>
          </div>
          {activeLayer ? (
            <Popover
              id={id}
              disableEnforceFocus={true}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "center",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "center",
                horizontal: -135
              }}
            >
              <PopoverContent layer={activeLayer} handleClose={handleClose} />
            </Popover>
          ) : null}
        </div>
      </div>
    </div>
  );
}
